import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { getCurrentPage, modalState, toggleModal } from 'reducers/ui';
import { connect, useSelector } from 'react-redux';
import useFilters from 'containers/Base/useFilters';
import { getSecurityId, getUserAssignedDepartmentList } from 'reducers/users';
import { getSecurityCompanyServices } from 'reducers/securityCompany';
import {
  workOrderFilterActions,
  workOrderGridTableActions,
  workOrderSelectors,
  workOrdersNS,
} from '../';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import { TASK_STATUS_FILTERS } from 'utils/constants/task';
import staticText from 'static/text_display.json';

const WorkOrdersPageLayout = ({
  cols,
  downloadable,
  workOrderTask,
  editable,
  filterValues,
  fetchWorkOrderTaskQR,
  fetchWorkOrderTaskList,
  totalEntries,
  forceRefresh,
  defaultFilterValues,
  customFetchTaskList,
  isTemplate,
  filters,
  filtersRequired,
  userAssignedDepartments,
  companyId,
  services,
  FormComponent,
  toggleModal,
  modalOpen,
  DetailComponent,
  fetchWorkOrderTaskListPdf,
  setFilterValues,
  setFilterKey,
  loading,
  forceOpenDetailPanel,
  persistentStateName,
}) => {
  const fetchTaskList = customFetchTaskList || fetchWorkOrderTaskList;

  const [maintenanceTaskGridApi, setMaintenanceTaskGridApi] = useState();
  const { localFilterValues, updateFilter } = useFilters(
    !isTemplate
      ? {
          filterValues: {
            ...filterValues,
            status: defaultFilterValues.status,
            taskType: defaultFilterValues.taskType,
            taskCategory: defaultFilterValues.taskCategory,
          },
        }
      : { filterValues: { ...defaultFilterValues, ...filterValues } },
  );

  const gridAPIHandler = gridAPI => {
    setMaintenanceTaskGridApi(gridAPI);
  };

  const onMapSelectedWorkeOrderId = useSelector(state =>
    workOrderSelectors.getSelectedRowID(state),
  );
  const workOrderTaskHighLight = onMapSelectedWorkeOrderId => {
    if (onMapSelectedWorkeOrderId != null) {
      const node =
        maintenanceTaskGridApi &&
        maintenanceTaskGridApi.getRowNode(onMapSelectedWorkeOrderId);
      // Go to the highlighted node
      maintenanceTaskGridApi &&
        maintenanceTaskGridApi.ensureNodeVisible(node, {
          position: 'top',
        });
      if (node) {
        if (!node.isSelected()) {
          node.setSelected(true);
        }
      }
    }
  };

  workOrderTaskHighLight(onMapSelectedWorkeOrderId);

  const fetchWorkOrderTask = from => {
    fetchTaskList({
      filters: localFilterValues,
      from,
    });
  };

  const getListData = workOrderTask => {
    return [
      ...workOrderTask.map(p => ({
        ...p,
        btnData: { id: p.id, onClick: id => fetchWorkOrderTaskQR({ id }) },
      })),
    ];
  };
  const onUpdate = filters => {
    fetchTaskList({ filters });
  };

  const onDownloadClick = () => {
    fetchWorkOrderTaskListPdf({ ...defaultFilterValues, ...filterValues });
  };

  return (
    <LayoutWithMap
      header="Tasks"
      cols={cols}
      downloadable={downloadable}
      {...(downloadable && {
        onDownloadClick: onDownloadClick,
        downloadHoverText: staticText.reporting_view.task_download,
      })}
      onEdit={modalOpen}
      toggleModal={toggleModal}
      data={getListData(workOrderTask)}
      // for creation / edit
      filters={filters({
        filterValues: localFilterValues,
        maintenanceStatusFilters: TASK_STATUS_FILTERS,
        taskTypeFilters: defaultFilterValues.taskType,
        securityCompanyId: companyId,
        userAssignedDepartments,
        services,
      })}
      loading={loading}
      setFilters={(values, hasApplyFilter) =>
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: vals => {
            setFilterValues(vals);
            setFilterKey(true);
          },
        })
      }
      DetailComponent={DetailComponent}
      FormComponent={FormComponent}
      onUpdate={onUpdate}
      filtersRequired={filtersRequired}
      filterValues={localFilterValues}
      rowModelType="infinite"
      editable={editable}
      fetchData={fetchWorkOrderTask}
      forceRefresh={forceRefresh}
      totalEntries={totalEntries}
      setGridAPI={gridAPIHandler}
      defaultFilterValues={defaultFilterValues}
      createTextHover={staticText.maintenance_operational_view.task.create}
      persistentStateName={persistentStateName}
      forceOpenDetailPanel={forceOpenDetailPanel}
    />
  );
};

WorkOrdersPageLayout.propTypes = {
  onEdit: PropTypes.bool,
  downloadable: PropTypes.bool,
  toggleModal: PropTypes.func,
  setMaintainerTaskFilterKey: PropTypes.func,
  editable: PropTypes.bool,
  workOrderTask: PropTypes.array,
  services: PropTypes.array,
  filters: PropTypes.func,
  modalOpen: PropTypes.bool,
  setFilters: PropTypes.func,
  filterValues: PropTypes.object,
  filtersRequired: PropTypes.bool,
  loading: PropTypes.bool,
  DetailComponent: PropTypes.elementType,
  currentPage: PropTypes.string,
  currentView: PropTypes.string,
  companyId: PropTypes.string,
  fetchWorkOrderTaskList: PropTypes.func,
  totalEntries: PropTypes.number,
  forceRefresh: PropTypes.bool,
  forceOpenDetailPanel: PropTypes.bool,
  setRefreshMaintainerTask: PropTypes.func,
  fetchWorkOrderTaskQR: PropTypes.func,
  fetchWorkOrderTaskListPdf: PropTypes.func,
  setFilterValues: PropTypes.func,
  FormComponent: PropTypes.object,
  setFilterKey: PropTypes.func,
  enhancedCancellation: PropTypes.func,
  fetchMaintainerTaskQr: PropTypes.func,
  secondaryColumn: PropTypes.object,
  cols: PropTypes.array,
  fetchMaintainerTaskListPdf: PropTypes.func,
  defaultFilterValues: PropTypes.object,
  isTemplate: PropTypes.bool,
  customFetchTaskList: PropTypes.func,
  operationComponent: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  map: PropTypes.object,
  resetMaintenanceTaskDetail: PropTypes.func,
  userAssignedDepartments: PropTypes.array,
  persistentStateName: PropTypes.string,
};

const mapStateToProps = state => ({
  workOrderTask: workOrderSelectors.getWorkOrderListTransformed(state),
  filterValues: workOrderSelectors.getWorkOrderFilters(state),
  totalEntries: workOrderSelectors.getTotalRowsCount(state),
  forceRefresh: workOrderSelectors.getForceRefresh(state),
  currentPage: getCurrentPage(state),
  companyId: getSecurityId(state),
  services: getSecurityCompanyServices(state),
  userAssignedDepartments: getUserAssignedDepartmentList(state),
  modalOpen: modalState(state)[workOrdersNS],
});

const mapDispatchToProps = dispatch => ({
  fetchWorkOrderTaskList: ({ filters, from }) =>
    dispatch(workOrderGridTableActions.fetchGridList({ filters, from })),
  toggleModal: () => dispatch(toggleModal(workOrdersNS)),
  fetchWorkOrderTaskQR: ({ id }) =>
    dispatch(workOrderGridTableActions.fetchQr({ id })),
  fetchWorkOrderTaskListPdf: filters =>
    dispatch(workOrderGridTableActions.fetchGridListPdf({ filters })),
  setFilterValues: filters =>
    dispatch(workOrderFilterActions.setFilters(filters)),
  setFilterKey: val => dispatch(workOrderFilterActions.setFilterKey(val)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(WorkOrdersPageLayout);
